// @ts-nocheck
import { useState, useRef, useEffect, useMemo } from 'react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { MoreVertical, Plus, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/api-client';
import { useMembers } from '@/hooks/queries/useAdmin';
import { useAuthStore } from '@/stores/authStore';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDialerUI } from '@/contexts/DialerUIContext';
import { useDialer } from '@/contexts/DialerContext';
import { useTranslation } from 'react-i18next';
import AddProspectDialog from '@/components/dialer/AddProspectDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import { ReasonTooltip } from '@/components/shared/ReasonTooltip';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { fetchAllProspectPages, invalidateProspectListAllPages, ENGINE_QUEUE_LIMIT } from '@/hooks/useProspectListBeyondCap';
import { runBulk, isNotFound } from '@/services/dialer/bulkRun';

/** Parallel DELETEs when a whole list goes (REV-W4-F2): enough to be quick, few enough for the rate limiter. */
const LIST_DELETE_CONCURRENCY = 5;

/**
 * XDialTopBar, VF design.
 * Screenshot ref : Page accueil/dialer dark mode (onglets listes scrollable,
 *                  badge count, overflow +N, indicator HubSpot, XP, notification bell).
 */
const MAX_VISIBLE_LISTS = 5;

export default function XDialTopBar({
  sources,
  selectedSource,
  onSourceChange,
  prospectsBySource,
  prospectQueue = [],
  onViewChange,
  selectedView,
  callerData,
  latestCallByPhone,
}) {
  const { t } = useTranslation(['dialer', 'common']);
  const { selectedCustomerId } = useDialer() as { selectedCustomerId?: string };
  const queryClient = useQueryClient();
  // #91 — source des agents conforme a l'ADR-031 : les Memberships via
  // useMembers, PAS l'entite Caller (collection TeamMember abandonnee).
  const activeOrgId = useAuthStore((st) => st.activeMembership?.organisationId);
  const { data: teamMembers = [] } = useMembers(activeOrgId);
  const { importDialogOpen, setImportDialogOpen } = useDialerUI();
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [renamingList, setRenamingList] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deletingList, setDeletingList] = useState(null);
  // AUDIT-F23: deleting a list deletes its prospects, a tenant admin's call.
  const { isTenantAdmin } = usePermissions();
  // QA-W7-2: permissions_v2 may remove records.write from the role.
  const writeBlocked = usePermissionGuard()('records.write');

  // ── Correction #91 — proprietaire de liste editable depuis le dialer ─────
  // Il fallait passer par l'administration pour savoir a qui appartient une
  // liste, et pour la reassigner. Le menu ⋮ de l'onglet le fait maintenant.
  // The owner is shared by the organisation (ListSetting, one row per list
  // name). It used to live in each browser's localStorage: nobody else saw it.
  const { data: listSettings = [], isSuccess: listSettingsLoaded } = useQuery({
    queryKey: ['list-settings'],
    queryFn: () => api.entities.ListSetting.list(),
    staleTime: 30_000,
  });
  const listOwners = useMemo(
    () => Object.fromEntries((listSettings as Array<{ source: string; owner: string }>).map((s) => [s.source, s.owner])),
    [listSettings],
  );
  const setListOwner = async (source, owner) => {
    try {
      const existing = (listSettings as Array<{ id: string; source: string }>).find((s) => s.source === source);
      if (existing) await api.entities.ListSetting.update(existing.id, { owner });
      else await api.entities.ListSetting.create({ source, owner });
      await queryClient.invalidateQueries({ queryKey: ['list-settings'] });
    } catch {
      toast.error(t('dialer:topBar.listOwnerError'));
    }
  };
  // One-time move of owners set before this change, then the local copy goes.
  useEffect(() => {
    if (!listSettingsLoaded) return;
    let local = null;
    try { local = JSON.parse(localStorage.getItem('xdial-list-owners') || 'null'); } catch { /* ignore */ }
    if (!local || typeof local !== 'object') return;
    try { localStorage.removeItem('xdial-list-owners'); } catch { /* ignore */ }
    Object.entries(local).forEach(([source, owner]) => {
      if (owner && !listOwners[source]) void setListOwner(source, String(owner));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listSettingsLoaded]);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [creatingList, setCreatingList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const newListInputRef = useRef(null);

  useEffect(() => {
    if (creatingList && newListInputRef.current) newListInputRef.current.focus();
  }, [creatingList]);

  const handleCreateList = async (name) => {
    const trimmed = name.trim();
    if (!trimmed) { setCreatingList(false); return; }
    if (allSources.includes(trimmed)) {
      toast.error(t('dialer:topBar.listAlreadyExists'));
      onSourceChange(trimmed);
      setCreatingList(false);
      setNewListName('');
      return;
    }
    // The list only exists once its placeholder row is saved, under the
    // active customer (the queue is filtered by customer, so a row without it
    // never showed up). The error used to be swallowed behind a success toast.
    try {
      await api.entities.ProspectQueue.create({
        prospect_name: '',
        prospect_phone: '',
        source: trimmed,
        status: 'placeholder',
        customer_id: selectedCustomerId || '',
      });
      await queryClient.invalidateQueries({ queryKey: ['prospect-queue'] });
    } catch (_e) {
      toast.error(t('dialer:topBar.listCreateError'));
      return;
    }
    onSourceChange(trimmed);
    toast.success(t('dialer:topBar.listCreated', { name: trimmed }));
    setCreatingList(false);
    setNewListName('');
  };

  const showTabs = selectedView === 'queue';
  const realSources = sources && sources.length > 0 ? sources : [];
  const allSources = selectedSource && !realSources.includes(selectedSource) ? [...realSources, selectedSource] : realSources;
  // ── Correction #26 — ordre des listes reordonnable par glisser-deposer ───
  // L'ordre etait celui du backend. Un commercial qui appelle toujours la meme
  // liste en premier devait la chercher a chaque fois. Ordre memorise par
  // utilisateur ; toute liste nouvelle ou inconnue reste a sa place d'origine.
  const [tabOrder, setTabOrder] = useState/* #26 */(() => {
    try { return JSON.parse(localStorage.getItem('xdial-list-order') || '[]'); } catch { return []; }
  });
  const [draggedTab, setDraggedTab] = useState(null);
  const orderedSources = useMemo(() => {
    if (!tabOrder.length) return allSources;
    const known = tabOrder.filter((sname) => allSources.includes(sname));
    const rest = allSources.filter((sname) => !known.includes(sname));
    return [...known, ...rest];
  }, [allSources, tabOrder]);
  const moveTab = (from, to) => {
    if (from === to) return;
    // L'index cible se calcule sur le tableau AVANT retrait : sinon, deplacer
    // vers la droite reinsere l'element a sa propre place et rien ne bouge.
    const toIndex = orderedSources.indexOf(to);
    const next = [...orderedSources];
    const [moved] = next.splice(orderedSources.indexOf(from), 1);
    next.splice(toIndex, 0, moved);
    setTabOrder(next);
    try { localStorage.setItem('xdial-list-order', JSON.stringify(next)); } catch { /* stockage indisponible */ }
  };

  const hasOverflow = allSources.length > MAX_VISIBLE_LISTS;
  const visibleSources = hasOverflow ? orderedSources.slice(0, MAX_VISIBLE_LISTS) : orderedSources;
  const hiddenSources = hasOverflow ? orderedSources.slice(MAX_VISIBLE_LISTS) : [];
  const activeInHidden = hiddenSources.includes(selectedSource);
  const finalVisible = activeInHidden
    ? [...visibleSources.slice(0, MAX_VISIBLE_LISTS - 1), selectedSource]
    : visibleSources;
  const finalHidden = activeInHidden
    ? [visibleSources[MAX_VISIBLE_LISTS - 1], ...hiddenSources.filter(s => s !== selectedSource)]
    : hiddenSources;

  const getCount = (source) => {
    if (!prospectsBySource || !prospectsBySource[source]) return 0;
    return prospectsBySource[source].length;
  };

  const getCompletion = (source) => {
    const srcProspects = prospectsBySource?.[source] || [];
    if (!srcProspects.length || !latestCallByPhone) return { called: 0, total: 0, pct: 0, segments: [] };
    let connected = 0, attempted = 0, voicemail = 0, pending = 0;
    srcProspects.forEach(p => {
      const lastCall = latestCallByPhone[p.prospect_phone];
      if (!lastCall) { pending++; return; }
      const outcome = lastCall.call_outcome || lastCall.call_status || '';
      if (['contacted', 'connected', 'meeting_booked', 'connected_to_agent'].includes(outcome)) connected++;
      else if (['voicemail', 'left_voicemail'].includes(outcome)) voicemail++;
      else if (['no_answer', 'not_reachable', 'busy', 'failed', 'not_interested'].includes(outcome)) attempted++;
      else pending++;
    });
    const total = srcProspects.length;
    const called = connected + attempted + voicemail;
    return {
      called, total, pct: Math.round((called / total) * 100),
      segments: [
        { count: connected, color: 'var(--vf-green)',  label: t('dialer:topBar.segments.connected') },
        { count: attempted, color: 'var(--vf-orange)', label: t('dialer:topBar.segments.attempted') },
        { count: voicemail, color: 'var(--vf-accent)', label: t('dialer:topBar.segments.voicemail') },
        { count: pending,   color: 'var(--vf-text-4)', label: t('dialer:topBar.segments.pending') },
      ],
    };
  };

  const handleRename = async (source, newName) => {
    if (!newName.trim() || newName.trim() === source) {
      setRenamingList(null);
      return;
    }
    const allForSource = prospectQueue.filter(p => p.source === source);
    for (const p of allForSource) {
      await api.entities.ProspectQueue.update(p.id, { source: newName.trim() });
    }
    queryClient.invalidateQueries({ queryKey: ['prospect-queue'] });
    invalidateProspectListAllPages(queryClient);
    onSourceChange(newName.trim());
    toast.success(t('dialer:topBar.listRenamed', { name: newName.trim() }));
    setRenamingList(null);
  };

  // REV-W4-F2: up to 20 000 DELETEs used to go out in one Promise.all; the
  // first 429 rejected the whole deletion while the others kept running, and
  // the agent did not know what was left. A small pool now, 404 = already
  // gone; a partial failure says « n supprimés sur N » and offers Réessayer
  // on the failed contacts only (like the list's bulk delete).
  const deleteListRows = async (source, rows) => {
    const { ok, failed } = await runBulk(rows.map((p) => p.id), (id) => api.entities.ProspectQueue.delete(id), isNotFound, { concurrency: LIST_DELETE_CONCURRENCY });
    queryClient.invalidateQueries({ queryKey: ['prospect-queue'] });
    invalidateProspectListAllPages(queryClient);
    setDeletingList(null);
    if (failed.length > 0) {
      const failedIds = new Set(failed);
      toast.error(t('dialer:topBar.deletePartial', { done: ok.length, total: rows.length, count: failed.length }), {
        action: { label: t('common:actions.retry'), onClick: () => { void deleteListRows(source, rows.filter((p) => failedIds.has(p.id))); } },
      });
      return;
    }
    toast.success(t('dialer:topBar.listDeleted', { name: source, count: rows.filter(p => p.status !== 'placeholder').length }));
    const remaining = allSources.filter(s => s !== source);
    if (remaining.length > 0) onSourceChange(remaining[0]);
  };

  const handleDeleteList = async (source) => {
    if (deleteInProgress) return;
    setDeleteInProgress(true);
    try {
      // T-d3: the loaded queue stops at ENGINE_QUEUE_LIMIT. Past it, the
      // list's contacts are read through the paginated route, so deleting a
      // list deletes all of it, not the part that happened to be loaded
      // (BUGS-OUVERTS.md, LIST-CAP-DELETE-1).
      let allForSource = prospectQueue.filter(p => p.source === source);
      if (prospectQueue.length >= ENGINE_QUEUE_LIMIT) {
        const all = await fetchAllProspectPages({ source, ...(selectedCustomerId ? { customer_id: selectedCustomerId } : {}) });
        if (!all.complete) throw new Error(t('dialer:topBar.deleteTooLarge'));
        allForSource = all.items;
      }
      await deleteListRows(source, allForSource);
    } catch (err) {
      toast.error(t('dialer:topBar.deleteError', { message: err?.message || err }));
    } finally {
      setDeleteInProgress(false);
    }
  };

  return (
    <>
      <div
        // Same side gutter as the page content below (px-3, md:px-5): with
        // 8px here the list row started 12px left of the title and table.
        // flex-wrap + minHeight (QA-W4-7): at 390 px the list actions
        // (Colonnes, Script) wrap to a second line instead of being cut.
        className="flex flex-wrap items-center flex-shrink-0 min-w-0 px-3 md:px-5"
        style={{
          minHeight: 44,
          rowGap: 6,
          background: 'var(--vf-bg)',
          borderColor: 'var(--vf-border)',
          gap: 4,
        }}
      >
        {/* "+" Add prospects */}
        {showTabs && (
          <div className="relative flex-shrink-0" style={{ marginRight: 6 }}>
            <ReasonTooltip reason={writeBlocked}>
            <AddProspectDialog
              clientEmail={callerData?.client_email}
              defaultSource={selectedSource}
              existingSources={sources}
              externalOpen={importDialogOpen}
              onExternalOpenChange={setImportDialogOpen}
              onProspectAdded={() => { void queryClient.invalidateQueries({ queryKey: ['prospect-queue'] }); invalidateProspectListAllPages(queryClient); }}
              trigger={
                <button
                  disabled={!!writeBlocked}
                  style={{
                    opacity: writeBlocked ? 0.5 : 1,
                    height: 28,
                    borderRadius: 8,
                    border: 'none',
                    background: 'var(--vf-primary)',
                    color: 'var(--vf-primary-fg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: writeBlocked ? 'not-allowed' : 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '0 10px 0 8px',
                    gap: 4,
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--vf-primary-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--vf-primary)'; }}
                >
                  <Plus size={13} strokeWidth={2.5} />
                  <span>{t('dialer:topBar.addList')}</span>
                </button>
              }
            />
            </ReasonTooltip>
          </div>
        )}

        {/* Create list button */}
        {showTabs && !creatingList && (
          <ReasonTooltip reason={writeBlocked}>
          <button
            type="button"
            aria-label={t('dialer:topBar.createList')}
            disabled={!!writeBlocked}
            onClick={() => { setCreatingList(true); setNewListName(''); }}
            style={{
              height: 28, width: 28, borderRadius: 8,
              border: '1px dashed var(--vf-border-hi)',
              background: 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--vf-text-3)',
              flexShrink: 0,
            }}
            title={writeBlocked ? undefined : t('dialer:topBar.createList')}
          >
            <Plus size={14} />
          </button>
          </ReasonTooltip>
        )}
        {showTabs && creatingList && (
          <input
            ref={newListInputRef}
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            onBlur={() => handleCreateList(newListName)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.target.blur();
              if (e.key === 'Escape') { setCreatingList(false); setNewListName(''); }
            }}
            placeholder={t('dialer:topBar.listNamePlaceholder')}
            className="vf-font-mono outline-none flex-shrink-0"
            style={{
              height: 28,
              fontSize: 11,
              width: 140,
              padding: '0 8px',
              borderRadius: 6,
              border: '1px solid var(--vf-accent-bd)',
              background: 'var(--vf-surface-2)',
              color: 'var(--vf-text-1)',
            }}
          />
        )}

        {/* List tabs */}
        {showTabs && (
          <div data-tour="source-tabs" className="flex items-center flex-1 min-w-0 overflow-x-auto" style={{ gap: 3 }}>
            {finalVisible.map((source) => {
              const isActive = selectedSource === source;
              const { called, total, segments } = getCompletion(source);
              const label = source.length > 18 ? source.substring(0, 18) + '…' : source;
              const count = total;

              return (
                <div
                  key={source}
                  className="flex items-center flex-shrink-0"
                  draggable={renamingList !== source}
                  onDragStart={() => setDraggedTab(source)}
                  onDragEnd={() => setDraggedTab(null)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); if (draggedTab) moveTab(draggedTab, source); setDraggedTab(null); }}
                  style={{ opacity: draggedTab === source ? 0.45 : 1, cursor: renamingList === source ? 'text' : 'grab' }}
                  title={t('dialer:topBar.dragToReorder')}
                >
                  {renamingList === source ? (
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => handleRename(source, renameValue)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.target.blur();
                        if (e.key === 'Escape') setRenamingList(null);
                      }}
                      className="outline-none"
                      style={{
                        height: 28,
                        fontSize: 11,
                        width: 130,
                        padding: '0 8px',
                        borderRadius: 6,
                        border: '1px solid var(--vf-accent-bd)',
                        background: 'var(--vf-surface-2)',
                        color: 'var(--vf-text-1)',
                      }}
                    />
                  ) : (
                    <button
                      onClick={() => onSourceChange(source)}
                      title={segments.map(seg => `${seg.label}: ${seg.count}`).join(' · ')}
                      className="flex items-center flex-shrink-0 relative overflow-hidden group/tab"
                      style={{
                        height: 28,
                        padding: '0 10px',
                        borderRadius: 8,
                        border: 'none',
                        background: isActive ? 'var(--vf-surface-3)' : 'transparent',
                        color: isActive ? 'var(--vf-text-1)' : 'var(--vf-text-3)',
                        fontSize: 12,
                        fontWeight: isActive ? 700 : 500, // #90 — la liste active se distingue franchement
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        gap: 6,
                        transition: 'all 0.12s',
                      }}
                      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--vf-surface-2)'; }}
                      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                    >
                      {/* Progression segments - bas */}
                      {isActive && total > 0 && (
                        <div className="absolute bottom-0 left-0 right-0" style={{ height: 2, display: 'flex' }}>
                          {segments.map((seg, i) =>
                            seg.count > 0 ? (
                              <div
                                key={i}
                                style={{ width: `${(seg.count / total) * 100}%`, background: seg.color }}
                                title={`${seg.label}: ${seg.count}`}
                              />
                            ) : null
                          )}
                        </div>
                      )}
                      <span>{label}</span>
                      {count > 0 && (
                        <span
                          className="vf-font-mono"
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '1px 5px',
                            borderRadius: 4,
                            background: isActive ? 'var(--vf-accent-bg)' : 'var(--vf-surface-3)',
                            color: isActive ? 'var(--vf-accent)' : 'var(--vf-text-3)',
                          }}
                        >
                          {count}
                        </span>
                      )}
                    </button>
                  )}

                  {/* 3-dot menu */}
                  {isActive && !renamingList && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label={t('dialer:topBar.listActions', { name: source })}
                          className="flex items-center justify-center"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: 20, height: 20, borderRadius: 5,
                            background: 'transparent', border: 'none',
                            color: 'var(--vf-text-3)',
                            cursor: 'pointer',
                            marginLeft: 2,
                          }}
                        >
                          <MoreVertical size={12} aria-hidden />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-44">
                        <DropdownMenuItem onClick={() => { setRenamingList(source); setRenameValue(source); }}>
                          {t('dialer:topBar.renameList')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {/* #91 — proprietaire de la liste, choisi parmi les callers */}
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--vf-text-3)' }}>
                          {t('dialer:topBar.owner')}
                        </DropdownMenuLabel>
                        {(teamMembers && teamMembers.length > 0
                          ? teamMembers
                          : [{ id: callerData?.id, caller_name: callerData?.caller_name, caller_email: callerData?.caller_email }]
                        ).map((m) => {
                          const label = m.full_name || m.name || m.caller_name || m.email || m.caller_email || t('dialer:topBar.noName');
                          const current = listOwners[source] === label;
                          return (
                            <DropdownMenuItem key={m.id || label} onClick={() => setListOwner(source, label)}>
                              <span className="flex-1 truncate">{label}</span>
                              {current && <span style={{ color: 'var(--vf-green)' }}>✓</span>}
                            </DropdownMenuItem>
                          );
                        })}
                        {isTenantAdmin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              style={{ color: 'var(--vf-red)' }}
                              disabled={!!writeBlocked}
                              onClick={() => setDeletingList({ source, count: getCount(source) })}
                            >
                              {t('dialer:topBar.deleteList')}
                            </DropdownMenuItem>
                            {writeBlocked && <p className="px-2 pb-1.5 text-xs" style={{ color: 'var(--vf-text-3)', maxWidth: 240 }}>{writeBlocked}</p>}
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              );
            })}

            {/* +N overflow */}
            {finalHidden.length > 0 && (
              <DropdownMenu open={overflowOpen} onOpenChange={setOverflowOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={t('dialer:topBar.moreLists', { count: finalHidden.length })}
                    className="flex items-center flex-shrink-0"
                    style={{
                      height: 28,
                      padding: '0 10px',
                      borderRadius: 8,
                      border: '1px solid var(--vf-border)',
                      background: 'var(--vf-surface-2)',
                      color: 'var(--vf-text-2)',
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      gap: 3,
                    }}
                  >
                    +{finalHidden.length}
                    <ChevronDown size={11} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64 max-h-60 overflow-y-auto">
                  {finalHidden.map((source) => {
                    const { called, total, pct } = getCompletion(source);
                    return (
                      <DropdownMenuItem
                        key={source}
                        onClick={() => { onSourceChange(source); setOverflowOpen(false); }}
                        className="cursor-pointer text-xs flex justify-between"
                      >
                        <span className="truncate">{source}</span>
                        <span className="vf-font-mono ml-2" style={{ fontSize: 10, color: 'var(--vf-text-3)' }}>
                          {called}/{total} ({pct}%)
                        </span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}

        {!showTabs && <div className="flex-1" />}

        {/* The contacts screen portals its list actions here (view, columns,
            tools, settings) so they sit on the list row instead of taking a
            row of their own under the filters. */}
        <div className="flex items-center gap-2 shrink-0 pl-2">
          <div id="xdial-list-actions-tools" className="flex items-center gap-2" />
          <div id="xdial-list-actions-script" className="flex items-center gap-2" />
        </div>

        {/* Headless AddProspectDialog — monte hors vue queue pour que Home/autres
            ecrans puissent l'ouvrir via setImportDialogOpen(true). Le trigger visible
            (bouton "+") reste uniquement dans la toolbar queue (voir plus haut). */}
        {!showTabs && (
          <AddProspectDialog
            clientEmail={callerData?.client_email}
            defaultSource={selectedSource}
            existingSources={sources}
            externalOpen={importDialogOpen}
            onExternalOpenChange={setImportDialogOpen}
            onProspectAdded={() => { void queryClient.invalidateQueries({ queryKey: ['prospect-queue'] }); invalidateProspectListAllPages(queryClient); }}
            trigger={<span style={{ display: 'none' }} />}
          />
        )}

        {/* NotificationBell + avatar user deplaces dans UserHeaderBar (rendu
            par DialerLayout au-dessus des autres barres). */}
      </div>

      {/* Delete confirmation — DES-13: a list deletion is a bulk deletion,
          the list's name is typed to confirm (ConfirmDialog). The count is
          the loaded one; past the engine's cap it is not shown (T-d3). */}
      <ConfirmDialog
        open={!!deletingList}
        onOpenChange={(open) => { if (!open && !deleteInProgress) setDeletingList(null); }}
        title={t('dialer:topBar.deleteList')}
        description={deletingList
          ? (prospectQueue.length >= ENGINE_QUEUE_LIMIT
            ? `${t('dialer:topBar.deleteListConfirm')} (${deletingList.source})`
            : `${t('dialer:topBar.deleteListConfirm')} (${deletingList.source}, ${deletingList.count} ${t('common:entity.prospect', { count: deletingList.count })})`)
          : undefined}
        confirmLabel={deleteInProgress ? t('dialer:topBar.deleting') : t('common:actions.delete')}
        typedName={deletingList?.source}
        busy={deleteInProgress}
        onConfirm={() => (deletingList ? handleDeleteList(deletingList.source) : undefined)}
      />
    </>
  );
}
